import React, { useState } from "react";
import { Linkedin, Download, User, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ReCAPTCHA from "react-google-recaptcha";

interface HeroProps {
  name: string;
  description: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send the form data to Supabase edge function
      const { data, error } = await supabase.functions.invoke(
        "submit-contact-form",
        {
          body: {
            name: formData.name,
            email: formData.email,
            message: formData.message,
          },
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Message sent",
        description: "Your message has been sent successfully!",
      });

      // Reset form data
      setFormData({
        name: "",
        email: "",
        message: "",
      });

      onClose();
    } catch (error) {
      console.error("Error sending form:", error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
        <h2 className="text-2xl font-bold mb-2">Contact Me</h2>
        <h3 className="text-sm mb-2">
          Reach out if you want to have a discussion!
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#333333] text-white rounded-md hover:bg-[#222222]"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface CaptchaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: () => void;
}

const CaptchaModal: React.FC<CaptchaModalProps> = ({
  isOpen,
  onClose,
  onVerify,
}) => {
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const RECAPTCHA_SITE_KEY =
    import.meta.env.VITE_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaVerified(!!value);
  };

  const handleSubmit = () => {
    if (captchaVerified) {
      onVerify();
      onClose();
    } else {
      toast({
        title: "Verification Required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
        <h2 className="text-2xl font-bold mb-4">Verification Required</h2>
        <p className="mb-6 text-gray-700">
          Please complete the verification to download the CV.
        </p>
        <div className="flex justify-center mb-6">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 text-white rounded-md ${
              captchaVerified
                ? "bg-[#333333] hover:bg-[#222222]"
                : "bg-[#999999]"
            }`}
            disabled={!captchaVerified}
          >
            Download CV
          </button>
        </div>
      </div>
    </div>
  );
};

export const Hero: React.FC<HeroProps> = ({ name, description }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCaptchaModalOpen, setIsCaptchaModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openCaptchaModal = () => setIsCaptchaModalOpen(true);
  const closeCaptchaModal = () => setIsCaptchaModalOpen(false);

  const handleDownloadCV = async () => {
    try {
      setIsDownloading(true);

      // Fetch the CV file from Supabase Storage
      const { data, error } = await supabase.storage
        .from("website")
        .download("anon/anthony-clerc-cv.pdf");

      if (error) {
        throw error;
      }

      // Create a download URL from the blob
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "anthony-clerc-cv.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: "Anthony Clerc CV is on its way!",
        variant: "default",
      });
    } catch (error) {
      console.error("Error downloading CV:", error);
      toast({
        title: "Download Failed",
        description: "Couldn't download the CV. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="text-center max-w-[1000px] mb-10">
      <h1 className="text-7xl font-bold text-[#454545] mb-10 mt-5 max-md:text-[40px] max-sm:text-[32px]">
        {name}
      </h1>
      <p className="text-xl text-[#787878] leading-normal max-w-[900px] mb-[30px] mx-auto max-md:text-base max-sm:text-sm">
        {description}
      </p>
      <div className="flex gap-5 justify-center max-md:flex-wrap max-sm:flex-col max-sm:w-full">
        <a
          href="https://www.linkedin.com/in/anthonyclerc/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 font-medium no-underline transition-all bg-[#0077B5] text-white px-6 py-3 rounded-full hover:bg-[#0077B5]/90 max-sm:w-full max-sm:text-center"
        >
          <Linkedin size={20} />
          LinkedIn
        </a>
        <button
          onClick={openCaptchaModal}
          className="flex items-center justify-center gap-2 font-medium no-underline transition-all bg-[#333333] text-white px-6 py-3 rounded-full hover:bg-[#222222] max-sm:w-full max-sm:text-center"
        >
          <Download size={20} />
          Download CV
        </button>
        <button
          onClick={openModal}
          className="flex items-center justify-center gap-2 font-medium transition-all bg-[#999999] text-white px-6 py-3 rounded-full hover:bg-[#888888] max-sm:w-full max-sm:text-center"
        >
          <User size={20} />
          Contact
        </button>
      </div>

      <ContactModal isOpen={isModalOpen} onClose={closeModal} />
      <CaptchaModal
        isOpen={isCaptchaModalOpen}
        onClose={closeCaptchaModal}
        onVerify={handleDownloadCV}
      />
    </section>
  );
};
